import type { ReactNode } from "react";
import { LangLink as Link } from "@/components/LangLink";
import { Reveal } from "@/components/Reveal";
import { useTranslation } from "react-i18next";

type ProductItem = {
  name: string;
  displayName?: ReactNode;
  desc?: string;
  image?: string;
  img?: string;
  link?: string;
  to?: string;
  buttonText?: string;
};

type CategoryProductGridProps = {
  products: ProductItem[];
  titleMinHeight?: string;
  descMinHeight?: string;
  defaultButtonText?: string;
};

export function CategoryProductGrid({
  products,
  titleMinHeight,
  descMinHeight,
  defaultButtonText,
}: CategoryProductGridProps) {
  const { t } = useTranslation("common");
  const fallbackButtonText = defaultButtonText || t("learnMore", "Learn more");

  const gridCols =
    products.length === 2
      ? "grid gap-px bg-border sm:grid-cols-2"
      : products.length === 3
        ? "grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3"
        : "grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4";

  const imageAspect =
    products.length === 2
      ? "aspect-[21/9]"
      : products.length === 3
        ? "aspect-[16/10] md:aspect-[16/9]"
        : "aspect-square";

  return (
    <section className="container-bizu py-20">
      <Reveal stagger className={gridCols}>
        {products.map((p) => (
          <Link
            key={p.name}
            to={(p.link || p.to || "") as any}
            className="group relative flex h-full flex-col overflow-hidden bg-background transition-colors duration-500 hover:bg-muted/40"
          >
            {/* IMAGE */}
            {(p.image || p.img) && (
              <div className={`${imageAspect} overflow-hidden`}>
                <img
                  src={p.image || p.img}
                  alt={p.name}
                  loading="lazy"
                  width={500}
                  height={500}
                  className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                />
              </div>
            )}

            {/* CONTENT */}
            <div className="flex flex-1 flex-col p-8 lg:p-10">
              {/* TITLE */}
              <div
                className="mb-4 flex items-start"
                style={titleMinHeight ? { minHeight: titleMinHeight } : undefined}
              >
                <h3 className="display text-2xl leading-tight transition-colors group-hover:text-accent">
                  {p.displayName || p.name}
                </h3>
              </div>

              {/* DESCRIPTION */}
              {p.desc && (
                <div
                  className="flex"
                  style={descMinHeight ? { minHeight: descMinHeight } : undefined}
                >
                  <p className="text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                </div>
              )}

              {/* ACTION LINK */}
              <div className="mt-6 flex items-center justify-between">
                <span className="text-[13px] font-semibold uppercase tracking-[0.16em] text-white/70 transition-colors group-hover:text-accent">
                  {p.buttonText || fallbackButtonText}
                  <span className="sr-only"> about {p.displayName || p.name}</span>
                </span>

                <span className="flex items-center justify-center overflow-hidden">
                  <span className="relative h-5 w-5">
                    <span className="absolute inset-0 transition-all duration-300 group-hover:translate-x-2 group-hover:-translate-y-2 group-hover:opacity-0">
                      →
                    </span>
                    <span className="absolute inset-0 translate-x-[-10px] translate-y-[10px] opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100">
                      ↗
                    </span>
                  </span>
                </span>
              </div>

              {/* HOVER ACCENT LINE */}
              <div className="relative mt-4 h-px overflow-hidden bg-border">
                <div className="absolute inset-y-0 left-0 w-0 bg-accent transition-all duration-500 group-hover:w-full" />
              </div>
            </div>
          </Link>
        ))}
      </Reveal>
    </section>
  );
}
